import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import Button from "../Button/Button";
import CheckoutSkeleton from "./CheckoutSkeleton";
import "./CheckoutForm.scss";
import axiosClient from "../../axiosClient";
import { contactEmail } from "../../brand";

/** Stripe return URL used when a logged-in user pays from inside the app. */
export const UPGRADE_RETURN_PATH = "/complete-sign-up?upgrade=1";

/**
 * Checkout for an already logged-in user (trial upgrade or renewal).
 * Unlike CheckoutForm it creates no account: the backend uses the session's user.
 */
export default function AuthenticatedCheckoutForm(props: {
  products: string[];
  couponCode?: string;
  onComplete: () => void;
  /** The server refused because some selected products are already owned. */
  onAlreadyOwned?: (productIds: string[]) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null | undefined>(null);

  useEffect(() => {
    if (!errorMessage) return;
    document
      .getElementById("payment-error-message")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [errorMessage]);
  const [isLoading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const handleError = (error: { message?: string }) => {
    setLoading(false);
    setErrorMessage(error.message);
  };

  const handlePayNowButtonClicked = async () => {
    setErrorMessage(null);

    if (props.products.length === 0) {
      setErrorMessage("Please select at least one product.");
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        handleError(submitError);
        return;
      }

      const response = await axiosClient.post("/payments/oq-create-subscription-authenticated", {
        products: props.products,
        couponCode: props.couponCode?.trim() || undefined,
      });
      const { type, clientSecret, paymentIntentId, setupIntentId } = response.data;

      const confirmParams = {
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.origin}${UPGRADE_RETURN_PATH}`,
        },
        redirect: "if_required" as const,
      };

      const body: { paymentIntentId?: string; setupIntentId?: string } = {};
      if (type === "setup") {
        const { error, setupIntent } = await stripe.confirmSetup(confirmParams);
        if (error) {
          handleError(error);
          return;
        }
        body.setupIntentId = setupIntent?.id || setupIntentId;
      } else {
        const { error, paymentIntent } = await stripe.confirmPayment(confirmParams);
        if (error) {
          handleError(error);
          return;
        }
        body.paymentIntentId = paymentIntent?.id || paymentIntentId;
      }

      await axiosClient.post("/payments/oq-complete-subscription", body);
      props.onComplete();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400 || status === 402) {
        handleError({ message: err.response?.data?.error });
        return;
      }
      if (status === 409 && err.response?.data?.code === "ALREADY_OWNED") {
        handleError({ message: err.response.data.error });
        props.onAlreadyOwned?.(err.response.data.products ?? []);
        return;
      }
      console.error(err);
      handleError({ message: `Payment failed. Please contact ${contactEmail} for support` });
    }
  };

  return (
    <div id="CheckoutForm">
      <div className={`checkout-fields${isReady ? " checkout-fields--ready" : ""}`}>
        {!isReady && <CheckoutSkeleton />}
        <PaymentElement
          className="checkout-fields__element"
          options={{
            paymentMethodOrder: ["card"],
          }}
          onReady={() => setIsReady(true)}
        />
      </div>
      {errorMessage && (
        <div id="payment-error-message" role="alert">
          {errorMessage}
        </div>
      )}
      <Button
        text={isLoading ? "Processing ... " : "Pay now"}
        disabled={isLoading || !isReady || !stripe || !elements || props.products.length === 0}
        onClick={handlePayNowButtonClicked}
      ></Button>
    </div>
  );
}
